import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { X, Bell, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { logger } from "@/lib/logger";

/**
 * TopupOverlay — globally rendered fullscreen alert.
 *
 * Behaviour:
 * - Polls /api/topups/active every 60s + on focus.
 * - When an unrescued top-up exists for the logged-in user, renders a
 *   blocking fullscreen overlay with title + message + Close button.
 * - Clicking Close calls /api/topups/{id}/dismiss so the same user never
 *   sees the same message twice (persisted in db.topups.dismissed_by).
 */
export default function TopupOverlay() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [topup, setTopup] = useState(null);
  const [dismissing, setDismissing] = useState(false);

  const fetchActive = useCallback(async () => {
    if (!user) { setTopup(null); return; }
    try {
      const { data } = await api.get("/topups/active");
      setTopup(data || null);
    } catch (err) {
      if (err?.response?.status !== 401) logger.warn("topup fetch failed:", err);
    }
  }, [user]);

  useEffect(() => {
    fetchActive();
    const id = setInterval(fetchActive, 60_000);
    const onFocus = () => fetchActive();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, [fetchActive]);

  const dismiss = async (navigateTo = null) => {
    if (!topup) return;
    setDismissing(true);
    try {
      await api.post(`/topups/${topup.id}/dismiss`);
      setTopup(null);
      if (navigateTo) navigate(navigateTo);
    } catch (err) {
      logger.error("dismiss failed:", err);
    } finally {
      setDismissing(false);
    }
  };

  if (!topup) return null;
  const hasAction = !!topup.action_url;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      data-testid="topup-overlay"
      role="alertdialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-2xl bg-card border border-teal/50 rounded-2xl shadow-2xl p-8 md:p-12 relative animate-in fade-in zoom-in duration-200"
      >
        <div className="absolute top-4 right-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={dismiss}
            disabled={dismissing}
            data-testid="topup-close-btn"
            aria-label="Close"
          >
            <X size={20} />
          </Button>
        </div>

        <div className="flex flex-col items-center text-center gap-5">
          <div className="w-16 h-16 rounded-full bg-teal/10 text-teal flex items-center justify-center">
            <Bell size={32} strokeWidth={1.5} />
          </div>

          <h2
            className="heading text-3xl md:text-5xl font-semibold tracking-tight text-foreground"
            data-testid="topup-title"
          >
            {topup.title}
          </h2>

          <p
            className="text-base md:text-xl text-muted-foreground whitespace-pre-line max-w-xl"
            data-testid="topup-message"
          >
            {topup.message}
          </p>

          <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mt-2">
            From <span className="text-foreground font-medium">{topup.created_by_name}</span>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mt-2">
            <Button
              onClick={() => dismiss(null)}
              disabled={dismissing}
              variant="outline"
              className={`${hasAction ? "" : "bg-teal hover:bg-teal/90 text-white border-teal"} px-8 py-6 text-base`}
              data-testid="topup-close-cta"
            >
              {dismissing ? "Closing…" : (hasAction ? "Later" : "Close")}
            </Button>
            {hasAction && (
              <Button
                onClick={() => dismiss(topup.action_url)}
                disabled={dismissing}
                className="bg-teal hover:bg-teal/90 text-white px-8 py-6 text-base"
                data-testid="topup-action-cta"
              >
                {topup.action_label || "Open"}
                <ArrowRight size={16} className="ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
